/**
 * Warm PDP gallery images from product cards in the background.
 * Low priority / idle only — must not compete with visible card images.
 */
(function () {
  if (window.__productCardPrefetchInit) return;
  window.__productCardPrefetchInit = true;

  // Not on product pages — images are handled there already
  if (document.body?.classList?.contains('template-product')) return;

  const prefetched = new Set();
  const queue = [];
  let active = 0;
  const MAX_CONCURRENT = 2;

  const connection = navigator.connection || navigator.mozConnection || navigator.webkitConnection;
  if (connection?.saveData) return;
  if (connection?.effectiveType && /^(slow-2g|2g)$/.test(connection.effectiveType)) return;

  function warmUrl(url) {
    if (!url || prefetched.has(url)) return;
    prefetched.add(url);

    // Image() fills the HTTP cache used by the PDP <img> later
    return new Promise((resolve) => {
      const img = new Image();
      img.decoding = 'async';
      img.onload = img.onerror = () => resolve();
      try {
        img.fetchPriority = 'low';
      } catch (e) {
        // ignore
      }
      img.src = url;
    });
  }

  async function pump() {
    if (active >= MAX_CONCURRENT) return;
    const next = queue.shift();
    if (!next) return;
    active += 1;
    try {
      await warmUrl(next);
    } finally {
      active -= 1;
      if (queue.length) pump();
    }
  }

  function enqueue(urls, urgent) {
    const list = (urls || '')
      .split(',')
      .map((u) => u.trim())
      .filter(Boolean);

    list.forEach((url) => {
      if (prefetched.has(url) || queue.includes(url)) return;
      if (urgent) queue.unshift(url);
      else queue.push(url);
    });

    const start = () => {
      while (active < MAX_CONCURRENT && queue.length) pump();
    };

    if (urgent) {
      start();
      return;
    }

    if ('requestIdleCallback' in window) {
      requestIdleCallback(start, { timeout: 2000 });
    } else {
      setTimeout(start, 600);
    }
  }

  function bindCard(card) {
    if (!card || card.dataset.pdpPrefetchBound === '1') return;
    const urls = card.getAttribute('data-pdp-prefetch');
    if (!urls) return;
    card.dataset.pdpPrefetchBound = '1';

    observer.observe(card);

    const onIntent = () => enqueue(urls, true);
    card.addEventListener('pointerenter', onIntent, { once: true, passive: true });
    card.addEventListener('touchstart', onIntent, { once: true, passive: true });
    card.addEventListener('focusin', onIntent, { once: true });
  }

  const observer = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        const urls = entry.target.getAttribute('data-pdp-prefetch');
        enqueue(urls, false);
        observer.unobserve(entry.target);
      });
    },
    { rootMargin: '120px 0px', threshold: 0.01 }
  );

  function scan(root) {
    (root || document).querySelectorAll?.('[data-pdp-prefetch]')?.forEach(bindCard);
    if (root?.nodeType === 1 && root.matches?.('[data-pdp-prefetch]')) bindCard(root);
  }

  function scheduleScan(root) {
    if ('requestIdleCallback' in window) {
      requestIdleCallback(() => scan(root), { timeout: 1500 });
    } else {
      setTimeout(() => scan(root), 400);
    }
  }

  scheduleScan(document);

  const mo = new MutationObserver((mutations) => {
    for (const mutation of mutations) {
      mutation.addedNodes.forEach((node) => {
        if (node.nodeType !== 1) return;
        scheduleScan(node);
      });
    }
  });

  mo.observe(document.documentElement, { childList: true, subtree: true });
})();
